import { useEffect } from 'react';

import PageTypes from '@shared/constants/page-types';
import { loadGtmClient } from '@shared/data-access-frontend/bootstrap';
import { buildHotelLocation } from '@shared/utils-universal/hotel-location';
import { useMatches } from 'react-router';

import type { GoogleTagManagerProps } from '@shared/types/client-side-params';
import type { Culture } from '@shared/types/context';
import type { DealsPagePropsSerialized } from '@shared/types/deals-page-props';
import type { DetailsPagePropsSerialized } from '@shared/types/details-page-props';
import type { HotelContent } from '@shared/types/hotel-content';
import type { FormatDate } from '@shared/types/i18n/localisation-service';
import type { SearchPagePropsSerialized } from '@shared/types/search-page-props';
import type { UserContext } from '@shared/types/user-context';

type Props = {
  userContext: UserContext;
  culture: Culture;
  formatDate: FormatDate;
  enabled: boolean;
};

type RouteData = {
  props:
    | DealsPagePropsSerialized
    | SearchPagePropsSerialized
    | DetailsPagePropsSerialized;
};

const getHotelSpecialGtmProps = (uBFFHotel: HotelContent) => {
  const { addressRegion, city, country } = buildHotelLocation(
    uBFFHotel.location,
  );

  return {
    city,
    country,
    region: addressRegion,
    hotelId: `${uBFFHotel.hotelId}`,
    page: 'hotels-hotel-details',
  };
};

export const useLoadGtmClient = ({
  culture: { currency, locale, market },
  enabled,
  formatDate,
  userContext: {
    device: { isBrowser, isMobilePhone, isTablet },
    pageType,
  },
}: Props) => {
  const matches = useMatches();
  const { props } = matches[matches.length - 1].data as RouteData;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const { stay } = props;
    const gtmProps: GoogleTagManagerProps = {
      locale,
      market,
      currency,
      pageType,
      page: pageType,
      isMobile: !!isMobilePhone,
      isTablet: !!isTablet,
      isBrowser: !!isBrowser,
      checkInDate: formatDate(stay.checkIn),
      checkOutDate: formatDate(stay.checkOut),
      numberOfAdults: `${stay.numberOfAdults}`,
      numberOfRooms: `${stay.numberOfRooms}`,
      numberOfChildren: stay.childrenAges.length,
    };
    if (pageType === PageTypes.DETAILS) {
      Object.assign(
        gtmProps,
        getHotelSpecialGtmProps(
          (props as DetailsPagePropsSerialized).uBFFHotel,
        ),
      );
    }

    loadGtmClient(gtmProps);

    // We expect the GTM client to execute only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
